import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { Button, Tooltip, Icon } from 'antd'
import { Http } from '../../../helpers/axois'
import { useToast } from '../../../helpers/commonFunction'
import { Modal } from '../../../helpers/commonUi'
import { IApp } from './type'
import { AppState } from '../../../stateManager/store'
import style from './styles/marketplace-apps.module.css'

// icons
import { ReactComponent as UserIcon } from '../../../assets/svg/marketplace-apps/user-avatar.svg'
import { ReactComponent as EditIcon } from '../../../assets/svg/editIcon.svg'
import { ReactComponent as DeleteIcon } from '../../../assets/svg/delete.svg'
import { ReactComponent as OptionsIcon } from '../../../assets/svg/more.svg'
import { logger } from '../../../constant/winston'

interface IProps {
  removeApp: (id: string) => void
  updateApp: (id: string, data: any) => void
  app: IApp
}

export const AppCard: React.FC<IProps> = ({ app, removeApp, updateApp }) => {
  const history = useHistory()
  const orgId = useSelector((state: AppState) => state.org.selectedOrg?.id)
  const { SuccessToast, ErrorToast } = useToast()
  const { id: userId } = useSelector((state: AppState) => state.user.data.user)

  const [loading, setLoading] = useState(false)
  const [deleteModalVisibility, setDeleteModalVisibility] = useState(false)
  const [optionsVisibility, setOptionsVisibility] = useState(false)
  const orgPermissions: any = useSelector((state: any) => {
    return state.org.orgPermissions
}, (oldPermissions: any, newPermissions: any) => JSON.stringify(oldPermissions) === JSON.stringify(newPermissions))
const { selectedOrg } = useSelector((state: AppState) => state.org)
const loggedInUserId = useSelector((state: any) => {
    return state.user.data.user.id
})
  const {
    name,
    description,
    createdBy,
    createdAt,
    isInstalled,
    id,
    authorWebsite
  } = app
  const userName = createdBy.firstName + ' ' + createdBy.lastName
  const conditionArray = [userId === app.createdBy.id, isInstalled]
  const tooltipVisible = conditionArray.every((element) => element === false)

  const handleNavigate = (e: any) => {
    e.preventDefault();
    history.push(`/marketplace/apps/${id}/preview`)
  }

  const handleInstall = async (e: any) => {
    e.stopPropagation();
    if ((!orgPermissions?.includes("marketplace.PublishApp") || !orgPermissions.includes('org.View'))  && loggedInUserId!=selectedOrg?.owner) {
      ErrorToast("Not Authorized: Please request owner of this organization to provide access")
      return
    }
    if (app.isInstalled) {
      return
    }

    const endpoint = `/orgs/${orgId}/marketplaceApps/install/${id}`
    setLoading(true)
    try {
      await Http.post(endpoint).toPromise()
      updateApp(id, { isInstalled: true })
      SuccessToast('Successfully installed app')
    } catch (err) {
      var msg = 'Failed to install app'
      if (err === "Forbidden") {
        msg = "Not Authorized: Please request owner of this organization to provide access"
      }
      ErrorToast(msg)
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async () => {
    if ((!orgPermissions?.includes("marketplace.PublishApp") || !orgPermissions.includes('org.View'))  && loggedInUserId!=selectedOrg?.owner) {
      ErrorToast("Not Authorized: Please request owner of this organization to provide access")
      return
    }
    const endpoint = `/orgs/${orgId}/marketplaceApps/${id}`
    setDeleteModalVisibility(false)
    setLoading(true)

    try {
      await Http.delete(endpoint).toPromise()
      SuccessToast('Successfully deleted app')
      removeApp(id)
    } catch (err) {
      if (err === "Forbidden") {
        ErrorToast("Not Authorized: Please request owner of this organization to provide access")
      } else if (err instanceof Error) {
        const msg = err.message || 'Failed to delete app'
        ErrorToast(msg)
      } else {
        logger.warn("Failed to delete app")
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className={style.appCard}>
      <div style={{ cursor: "pointer" }} onClick={handleNavigate}>
        <div className={style.appCardHeader}>
          <h4 className="hl truncate text-ellipsis w-90% mr-3.5 pr-3.5" title={name}>{name}</h4>
          <p title={description}>{description}</p>
        </div>

        <div className="space-between">
          <p>
            <UserIcon /> {userName}
          </p>
          <p>{authorWebsite}</p>
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
          }}
        >
          <p>Created Date: {createdAt}</p>
          <Button shape="round" onClick={handleInstall} loading={loading}>
            {isInstalled ? 'Installed' : 'Install'}
          </Button>
        </div>
      </div>
      <Delete
        visible={deleteModalVisibility}
        setVisible={setDeleteModalVisibility}
        onOk={handleDelete}
        appName={app.name}
      />
      {!tooltipVisible && <div className={style.optionsWrapper}>
        <Tooltip
          title={() => (
            <Options
              app={app}
              removeApp={removeApp}
              updateApp={updateApp}
              setLoading={setLoading}
              setDeleteModalVisibility={setDeleteModalVisibility}
              hideOptions={() => setOptionsVisibility(false)}
            />
          )}
          placement="bottomRight"
          trigger="click"
          className="more"
          overlayClassName="widgetCardTooltip"
          visible={optionsVisibility}
          onVisibleChange={setOptionsVisibility}
        >
          <OptionsIcon width={16} height={16} />
        </Tooltip>
      </div>}
    </div>
  )
}

interface IOptions extends IProps {
  setLoading: (loading: boolean) => void
  setDeleteModalVisibility: (visible: boolean) => void
  hideOptions: () => void
}

const Options: React.FC<IOptions> = ({
  app,
  updateApp,
  setLoading,
  setDeleteModalVisibility,
  hideOptions,
}) => {
  const orgId = useSelector((state: AppState) => state.org.selectedOrg?.id)
  const userId = useSelector((state: AppState) => state.user.data.user.id)
  const { ErrorToast, SuccessToast } = useToast()
  const { id, isInstalled } = app
  const history = useHistory()
  const { selectedOrg } = useSelector((state: AppState) => state.org)
  const loggedInUserId = useSelector((state: any) => {
      return state.user.data.user.id
  })
  const orgPermissions: any = useSelector((state: any) => {
    return state.org.orgPermissions
}, (oldPermissions: any, newPermissions: any) => JSON.stringify(oldPermissions) === JSON.stringify(newPermissions))

  const handleEdit = () => {
    history.push(`/marketplace/apps/${id}/edit`)
  }

  const handleUninstall = async () => {
    if ((!orgPermissions?.includes("marketplace.PublishApp") || !orgPermissions.includes('org.View')) && loggedInUserId!=selectedOrg?.owner) {
      ErrorToast("Not Authorized: Please request owner of this organization to provide access")
      return
    }
    hideOptions()
    const endpoint = `/orgs/${orgId}/marketplaceApps/uninstall/${id}`
    setLoading(true)

    try {
      await Http.delete(endpoint).toPromise()
      SuccessToast('Successfully uninstalled app')
      updateApp(id, { isInstalled: false })
    } catch (err) {
      var msg = 'Failed to uninstall app'
      if (err === "Forbidden") {
        msg = "Not Authorized: Please request owner of this organization to provide access"
      }
      ErrorToast(msg)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className={style.options}>

      {userId === app.createdBy.id && (
        <span onClick={handleEdit}>
          <EditIcon width={16} height={16} />
          <span style={{ color: "#292d32" }}>Edit</span>
        </span>
      )}

      {isInstalled && (
        <span onClick={handleUninstall}>
          <DeleteIcon width={16} height={16} />
          <span style={{ color: "#292d32" }}>Uninstall</span>
        </span>
      )}

      {!isInstalled && userId === app.createdBy.id && (
        <span onClick={() => {
          hideOptions()
          setDeleteModalVisibility(true)
        }}>
          <DeleteIcon width={16} height={16} />
          <span style={{ color: "#292d32" }}>Delete</span>
        </span>
      )}
    </div>
  )
}

interface IDelete {
  visible: boolean
  setVisible: (visible: boolean) => void
  onOk: () => void
  appName: string
}

const Delete: React.FC<IDelete> = ({ visible, setVisible, onOk, appName }) => {
  return (
    <Modal
      id="deleteMarketplaceApp"
      title="DELETE APP"
      visible={visible}
      onCancel={() => setVisible(false)}
      unmountOnCancel={true}
      footer={{
        enable: true,
        okText: 'DELETE',
        cancelText: 'CANCEL',
        onOk,
      }}
    >
      <div className={style.deleteModalContent}>
        <p>Are you sure you want to delete the app <span>{appName}</span> ?</p>
      </div>
    </Modal>
  )
}
